/**
 * conversationRestore · 会话恢复与团队预热的 dispatch 编排
 *
 * 协议重定位 (2026-05 重构 · 阶段 1 + 阶段 2):
 *   · prewarmTeam              — 进入页面 / 切团队时跑一次, 调 init 拿团队级聚合数据 + welcome
 *   · restoreLastConversation  — 进入页面有 lastConvId / 切到历史会话时跑, 拉 history
 *     恢复消息气泡; folder 类消息再并发拉接口 5 把文件夹树和排除项回填。
 *
 * 两条链路完全解耦, 调用方按场景组合: 比如首次进入页面同时跑两个; 切历史会话只跑后者。
 * @module conversationRestore
 */

import type { MutableRefObject } from 'react'
import { fetchAiChatInit } from './aiChatInit'
import type { RawRecommendedPrompt } from './aiChatInit'
import type { RecommendedPrompt } from '../utils/recommendedPrompt'
import {
  fetchAiChatHistory,
  filterRenderableHistoryMessages,
  dedupTaskMessagesByTaskId,
  transformBackendMessage
} from './aiChatHistory'
import type { BackendChatMessage } from './aiChatHistory'
import {
  fetchAiChatHistoryFolders,
  previewFoldersToPlanFolders
} from './aiChatHistoryFolders'
import {
  setInitWelcome,
  initLoadingDone,
  messageAppended,
  assistantFoldersHydrated
} from '../../../slices/aiClassify'

type Dispatch = (action: unknown) => void
type MessagesList = Array<{ id: string; kind: string; role: string }>

/**
 * 团队级数据预热 · 调 init 拿 recommended_prompts / note, 写入 sessionSlice
 *
 * 调用时机:
 *   · 进入页面 (groupID 首次确定) 时跑一次
 *   · 切换团队 (groupID 变化) 时再跑一次
 *
 * 失败容忍: 网络/接口错时把 initLoading 置 false 让骨架屏退出，recommended_prompts
 * 走 FALLBACK_RECOMMENDED_PROMPTS 兜底（渲染层判定），不影响用户发首条消息
 * （send 接口跟 init 解耦）。
 */
export async function prewarmTeam(
  dispatch: Dispatch,
  groupID: string,
  signal?: AbortSignal
): Promise<void> {
  try {
    const resp = await fetchAiChatInit({ groupID, signal })
    // 后端 snake_case → 前端 camelCase 一次性转换；下游 EmptyState / QuickPrompts 只读 camelCase
    const recommended: RecommendedPrompt[] = Array.isArray(
      resp?.recommended_prompts
    )
      ? resp.recommended_prompts.map((p: RawRecommendedPrompt) => ({
          text: typeof p?.text === 'string' ? p.text : '',
          iconType: typeof p?.icon_type === 'string' ? p.icon_type : '',
          subtitle: typeof p?.subtitle === 'string' ? p.subtitle : ''
        }))
      : []
    const note = typeof resp?.note === 'string' ? resp.note : ''
    dispatch(setInitWelcome({ recommended, note }))
  } catch {
    // init 失败 / abort 走 fallback: 只结束 loading，recommended 由渲染层 FALLBACK 兜底
    dispatch(initLoadingDone())
  }
}

/**
 * 历史消息恢复 · 拉 /history 把指定会话的全部消息逐条写入 store
 *
 * 调用时机:
 *   · 进入页面发现 lastConvId 非空 (用户上次留下的会话) → 调本函数恢复
 *   · 用户从历史会话列表点选某条 → 调本函数恢复
 *
 * race guard: 如果调用期间用户已在前端发起新一轮 (messagesRef 非空), 跳过 dispatch
 * 避免历史消息跟用户刚发的消息互相混淆。
 *
 * @param processedRef 已处理消息 id 集合, 写入后防止 SSE effect 把历史 user 消息误认为
 *                     "未处理" 重新发起一轮请求
 */
export async function restoreLastConversation(
  dispatch: Dispatch,
  conversationId: string,
  groupID: string,
  processedRef: MutableRefObject<Set<string>>,
  messagesRef?: MutableRefObject<MessagesList>
): Promise<void> {
  const histResp = await fetchAiChatHistory(conversationId, groupID)
  // 协议约定: data == null 的消息 (预览操作占位 / 建夹完成回执 / 迁移前老消息) 跳过渲染。
  // pre-filter 后下游 transformBackendMessage 可以强假设 data 非空, 不再走老兼容路径。
  // 然后再按 task_id 折叠 task 类多行 (同一 build_task 后端会落两行 task 消息,
  // 防止 UI 出现重复的 build_task_status 卡)。
  const msgs = dedupTaskMessagesByTaskId(
    filterRenderableHistoryMessages(histResp?.messages ?? [])
  )
  if (msgs.length === 0) return

  if (messagesRef && messagesRef.current.length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      '[ai-classify] skip history dispatch · local round already in flight',
      { localMessageCount: messagesRef.current.length, histCount: msgs.length }
    )
    return
  }

  for (const m of msgs) {
    const { user, assistant } = transformBackendMessage(m)
    processedRef.current.add(user.id)
    if (user.content) dispatch(messageAppended(user))
    dispatch(messageAppended(assistant))
  }

  // folder 类消息的 plan 树通过接口 5 二次拉取后回填 (transformBackendMessage 同步阶段
  // 给的是空 folders 占位)。并发跑 + allSettled, 单条失败不阻塞其他消息渲染。
  await hydrateFolderMessages(dispatch, msgs, groupID)
}

/**
 * 并发拉所有 folder 类消息的接口 5 (历史快照文件夹树 + 排除项), 拿到后逐条 dispatch
 * 回填到对应 message.payload.card。
 *
 * 失败容忍:
 *   · 单条接口 5 失败 (404 / 网络抖动) 只输出 warn, 其他消息照常回填
 *   · 文档明示纯文本 message 调本接口会返 []/{} 不报错, 这里也容忍空 folders 的情况
 */
async function hydrateFolderMessages(
  dispatch: Dispatch,
  msgs: BackendChatMessage[],
  groupID: string
): Promise<void> {
  const folderMsgs = msgs.filter((m) => m.data?.type === 'folder')
  if (folderMsgs.length === 0) return

  await Promise.allSettled(
    folderMsgs.map(async (m) => {
      const backendMessageId = m.message_id ?? m.id
      try {
        const resp = await fetchAiChatHistoryFolders(backendMessageId, groupID)
        const folders = previewFoldersToPlanFolders(
          Array.isArray(resp?.folders) ? resp.folders : []
        )
        const excludedKeys = Array.isArray(resp?.excluded_folder_keys)
          ? resp.excluded_folder_keys
          : []
        const taskCreated = resp?.task_created === true
        dispatch(
          assistantFoldersHydrated({
            messageId: `hist-asst-${backendMessageId}`,
            folders,
            excludedKeys,
            taskCreated
          })
        )
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          '[ai-classify] hydrate folder message failed:',
          backendMessageId,
          err
        )
      }
    })
  )
}
